import { useEffect, useState } from "react";
import { Button, Card, Empty, Input, message } from "antd";
import { CircleCheck } from "lucide-react";

import { updateContractor } from "sr/utils/api/company/workorder/updateContractor";
import { uploadFile } from "sr/utils/api/uploadFile";

interface RequiredChecklistItem {
  name: string;
  isRequired?: boolean;
  isDocumentRequired?: boolean;
}

interface ChecklistCheck {
  checklistName: string;
  verified: boolean;
  uploadedDocument: string[];
}

interface Props {
  contractorId: string;
  requiredChecklist: RequiredChecklistItem[];
  value?: ChecklistCheck[];
  onBoardingStatus?: "pending" | "completed";
  onSaved?: (
    checks: ChecklistCheck[],
    status: "pending" | "completed",
    response?: unknown,
  ) => void;
  onBack?: () => void;
  finishLabel?: string;
  onChange?: (checks: ChecklistCheck[], status: "pending" | "completed") => void;
}

const normalizeChecks = (value: ChecklistCheck[] | string | undefined) => {
  let parsedValue: any = value;

  if (typeof parsedValue === "string") {
    try {
      parsedValue = JSON.parse(parsedValue);
    } catch {
      return {};
    }
  }

  if (parsedValue?.data?.onBordingChecklistCheck !== undefined) {
    parsedValue = parsedValue.data.onBordingChecklistCheck;
  } else if (parsedValue?.data?.onBoardingChecklistCheck !== undefined) {
    parsedValue = parsedValue.data.onBoardingChecklistCheck;
  } else if (parsedValue?.onBordingChecklistCheck !== undefined) {
    parsedValue = parsedValue.onBordingChecklistCheck;
  } else if (parsedValue?.onBoardingChecklistCheck !== undefined) {
    parsedValue = parsedValue.onBoardingChecklistCheck;
  }

  if (!Array.isArray(parsedValue)) {
    if (parsedValue && typeof parsedValue === "object") {
      const hasChecklistName =
        parsedValue.ChecklistName ||
        parsedValue.checklistName ||
        parsedValue.checklist_name ||
        parsedValue.name;
      parsedValue = hasChecklistName
        ? [parsedValue]
        : Object.entries(parsedValue).map(([name, check]: [string, any]) => ({
            ChecklistName: name,
            ...(check && typeof check === "object" ? check : {}),
          }));
    } else {
      parsedValue = [];
    }
  }

  const normalizedValues: any[] = parsedValue;
  return normalizedValues.reduce<Record<string, ChecklistCheck>>(
    (result, check) => {
      if (!check || typeof check !== "object") return result;

      const checklistName =
        check.ChecklistName ||
        check.checklistName ||
        check.checklist_name ||
        check.name;
      if (!checklistName) return result;

      const uploadedDocument =
        check.uploadedDocument ?? check.uploadedDocuments;
      result[String(checklistName)] = {
        checklistName: String(checklistName),
        verified: check.verified === true || check.isVerified === true,
        uploadedDocument: Array.isArray(uploadedDocument)
          ? uploadedDocument.filter(Boolean).map(String)
          : uploadedDocument
          ? [String(uploadedDocument)]
          : [],
      };
      return result;
    },
    {},
  );
};

const getDocumentName = (documentUrl: string) => {
  try {
    const fileName = decodeURIComponent(
      new URL(documentUrl).pathname.split("/").pop() || "",
    );
    return fileName || "Uploaded document";
  } catch {
    return documentUrl.split("/").pop() || "Uploaded document";
  }
};

export function OnboardingChecklist({
  contractorId,
  requiredChecklist,
  value = [],
  onBoardingStatus,
  onSaved,
  onBack,
  finishLabel = "Save",
  onChange,
}: Props) {
  const [checks, setChecks] = useState<ChecklistCheck[]>([]);
  const [checklistItems, setChecklistItems] = useState<RequiredChecklistItem[]>(
    [],
  );
  const [selectedStatus, setSelectedStatus] = useState<"pending" | "completed">(
    onBoardingStatus || "pending",
  );
  const [loading, setLoading] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Use sessionStorage key based on contractorId to persist across tab switches
  const storageKey = `onboarding_checklist_${contractorId}`;

  useEffect(() => {
    // Don't initialize if requiredChecklist is empty (still loading)
    if (!requiredChecklist || requiredChecklist.length === 0) return;

    // Try to load from sessionStorage first (for tab navigation persistence)
    const savedData = sessionStorage.getItem(storageKey);

    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setChecks(parsed.checks);
        setChecklistItems(parsed.checklistItems);
        setSelectedStatus(parsed.selectedStatus);
        setIsLoadingData(false);
        return;
      } catch (e) {
        // If parsing fails, continue with normal initialization
      }
    }

    // Normal initialization from props
    const existingChecks = normalizeChecks(value);
    const checklistItems = [...requiredChecklist];
    Object.keys(existingChecks).forEach((checklistName) => {
      if (!checklistItems.some((item) => item.name === checklistName)) {
        checklistItems.push({
          name: checklistName,
          isDocumentRequired:
            existingChecks[checklistName].uploadedDocument.length > 0,
        });
      }
    });
    setChecklistItems(checklistItems);
    const initialChecks = checklistItems.map((item) => ({
      checklistName: item.name,
      verified: existingChecks[item.name]?.verified === true,
      uploadedDocument: existingChecks[item.name]?.uploadedDocument || [],
    }));
    setChecks(initialChecks);
    setSelectedStatus(onBoardingStatus || "pending");
    setIsLoadingData(false);
  }, [requiredChecklist, value, onBoardingStatus, storageKey]);

  const updateCheck = (index: number, update: Partial<ChecklistCheck>) => {
    setChecks((current) => {
      const updated = current.map((check, checkIndex) =>
        checkIndex === index ? { ...check, ...update } : check,
      );
      // Save to sessionStorage for persistence across tab switches
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          checks: updated,
          checklistItems,
          selectedStatus,
        }),
      );
      // Notify parent if onChange callback is provided
      onChange?.(updated, selectedStatus);
      return updated;
    });
  };

  const uploadDocuments = async (index: number, files: FileList | null) => {
    if (!files?.length) return;

    setUploadingIndex(index);
    try {
      const uploadedUrls: string[] = [];
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append("task_img", file);
        const response = await uploadFile(formData);
        const location = response?.data?.files?.[0]?.location;
        if (location) {
          uploadedUrls.push(location);
        } else {
          message.error(`Unable to upload ${file.name}`);
        }
      }
      if (uploadedUrls.length) {
        updateCheck(index, {
          uploadedDocument: [
            ...(checks[index]?.uploadedDocument || []),
            ...uploadedUrls,
          ],
        });
      }
    } catch (error) {
      message.error("Unable to upload document");
    } finally {
      setUploadingIndex(null);
    }
  };

  const removeDocument = (checkIndex: number, documentIndex: number) => {
    const documents = checks[checkIndex]?.uploadedDocument || [];
    updateCheck(checkIndex, {
      uploadedDocument: documents.filter((_, index) => index !== documentIndex),
    });
  };

  const save = async () => {
    if (selectedStatus === "completed") {
      const missingRequired = checklistItems.find(
        (item, index) => item.isRequired && !checks[index]?.verified,
      );
      if (missingRequired) {
        message.error(
          `Please verify ${missingRequired.name} before proceeding`,
        );
        return;
      }

      const missingDocument = checklistItems.find(
        (item, index) =>
          item.isRequired &&
          item.isDocumentRequired &&
          !checks[index]?.uploadedDocument?.length,
      );
      if (missingDocument) {
        message.error(`Upload a document for ${missingDocument.name}`);
        return;
      }
    }

    setLoading(true);
    const payload: Record<string, unknown> = {
      id: contractorId,
      onBoardingChecklistCheck: checks,
    };
    if (onBoardingStatus !== undefined) {
      payload.onBoardingStatus = selectedStatus;
    }

    const response = await updateContractor(payload);
    setLoading(false);
    if (response?.success) {
      message.success("Onboarding checklist updated");
      // Clear sessionStorage after successful save
      sessionStorage.removeItem(storageKey);
      onSaved?.(checks, selectedStatus, response);
    } else {
      message.error("Unable to update onboarding checklist");
    }
  };

  return (
    <div className="mt-6 mx-auto max-w-4xl pb-40">
      {onBoardingStatus !== undefined && (
        <div className="mb-4 flex items-center justify-between rounded border border-gray-200 bg-white p-4">
          <label htmlFor="onboarding-status" className="font-semibold">
            Onboarding status
          </label>
          {isLoadingData ? (
            <div className="animate-pulse bg-gray-200 h-10 w-32 rounded"></div>
          ) : (
            <select
              id="onboarding-status"
              value={selectedStatus}
              onChange={(event) => {
                const newStatus = event.target.value as "pending" | "completed";
                setSelectedStatus(newStatus);
                // Save to sessionStorage
                sessionStorage.setItem(
                  storageKey,
                  JSON.stringify({
                    checks,
                    checklistItems,
                    selectedStatus: newStatus,
                  }),
                );
                // Notify parent if onChange callback is provided
                onChange?.(checks, newStatus);
              }}
              className="rounded border border-gray-300 px-3 py-2"
            >
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
            </select>
          )}
        </div>
      )}
      <Card title="Onboarding checklist" headStyle={{ padding: 10 }}>
        {isLoadingData ? (
          <div className="space-y-5">
            {[1, 2, 3].map((item) => (
              <div key={item} className="border-b pb-5">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="animate-pulse bg-gray-200 h-5 w-48 rounded mb-2"></div>
                    <div className="animate-pulse bg-gray-200 h-4 w-32 rounded"></div>
                  </div>
                  <div className="animate-pulse bg-gray-200 h-5 w-36 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : checklistItems.length ? (
          <div className="space-y-5">
            {checks.map((check, index) => {
              const item = checklistItems[index];
              return (
                <div key={check.checklistName} className="border-b pb-5">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="font-semibold">{item.name}</div>
                      <div className="text-sm text-gray-500">
                        {item.isRequired ? "Required" : "Optional"}
                        {item.isDocumentRequired &&
                          ` | Document ${
                            item.isRequired ? "required" : "optional"
                          }`}
                      </div>
                    </div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={check.verified}
                        onChange={(event) =>
                          updateCheck(index, { verified: event.target.checked })
                        }
                      />
                      Mark as complete
                    </label>
                  </div>
                  {item.isDocumentRequired && (
                    <div className="mt-3">
                      <input
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        disabled={uploadingIndex === index}
                        className="block w-full rounded border border-gray-300 p-2"
                        onChange={(event) => {
                          uploadDocuments(index, event.target.files);
                          event.target.value = "";
                        }}
                      />
                      <div className="mt-2 space-y-1">
                        {check.uploadedDocument.map(
                          (document, documentIndex) => (
                            <div
                              key={`${document}-${documentIndex}`}
                              className="flex items-center gap-2 text-sm"
                            >
                              <span className="text-gray-700 truncate">
                                {getDocumentName(document)}
                              </span>
                              <Button
                                type="link"
                                danger
                                size="small"
                                onClick={() =>
                                  removeDocument(index, documentIndex)
                                }
                              >
                                Remove
                              </Button>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <Empty description="No onboarding checklist configured" />
        )}
      </Card>
      <div className="fixed bottom-0 left-0 right-0 bg-white backdrop-blur-sm border-t border-gray-200 z-1000 p-2 !pl-0 sm:!pl-[200px]">
        <div className="flex items-center mx-auto sm:max-w-4xl">
          {onBack && (
            <Button size="large" danger className="!px-10" onClick={onBack}>
              Back
            </Button>
          )}
          <div className="flex-1" />
          <Button
            size="large"
            type="primary"
            className="!px-10"
            onClick={save}
            loading={loading}
          >
            {finishLabel} <CircleCheck size={20} />
          </Button>
        </div>
      </div>
    </div>
  );
}
